import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { field, lbl } from "./Modal";
import { Download, Mail, Trash2, CheckCircle, Circle, UploadCloud } from "lucide-react";

export function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  const load = () => client.get("/admin/subscribers").then(({ data }) => setSubs(data));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    const res = await client.get("/admin/subscribers/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "yaza-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-3xl">SUBSCRIBERS <span className="text-yaza-accent">{subs.length}</span></h2>
        <button onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 rounded-none border border-yaza-border px-5 py-2 text-xs font-bold uppercase tracking-widest hover:border-yaza-accent">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-hidden rounded-lg border border-yaza-border">
        {subs.length === 0 && <p className="p-6 text-sm text-neutral-500">No subscribers yet.</p>}
        {subs.map((s) => (
          <div key={s.id} className="flex items-center justify-between border-b border-yaza-border px-4 py-3 last:border-0">
            <span className="text-sm">{s.email}</span>
            <span className="text-xs text-neutral-500">{s.source} · {(s.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InquiriesTab() {
  const [items, setItems] = useState([]);
  const load = () => client.get("/admin/inquiries").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const markRead = async (i) => { await client.patch(`/admin/inquiries/${i.id}/read`); load(); };
  const del = async (i) => { await client.delete(`/admin/inquiries/${i.id}`); load(); };

  return (
    <div>
      <h2 className="mb-6 font-display text-3xl">INQUIRIES <span className="text-yaza-accent">{items.filter((i) => !i.read).length} new</span></h2>
      <div className="flex flex-col gap-3">
        {items.length === 0 && <p className="text-sm text-neutral-500">No inquiries yet.</p>}
        {items.map((i) => (
          <div key={i.id} data-testid={`inquiry-${i.id}`} className={`rounded-lg border p-4 ${i.read ? "border-yaza-border bg-transparent" : "border-yaza-accent/40 bg-yaza-accent/5"}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{i.name} <span className="text-xs text-neutral-500">{i.ig_handle}</span></p>
                {i.phone && <p className="text-xs text-neutral-500">{i.phone}</p>}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => markRead(i)} data-testid={`mark-read-${i.id}`} className="text-neutral-400 hover:text-yaza-ready">{i.read ? <CheckCircle size={16} /> : <Circle size={16} />}</button>
                <button onClick={() => del(i)} className="text-neutral-400 hover:text-yaza-accent"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-yaza-dim">{i.message}</p>
            <p className="mt-2 text-[11px] text-neutral-600">{(i.created_at || "").slice(0, 16).replace("T", " ")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BlastTab() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject || !body) return toast.error("Subject and body required");
    setSending(true);
    try {
      const { data } = await client.post("/admin/blast", { subject, body });
      toast.success(`Blast queued to ${data.sent} subscribers`);
      setSubject(""); setBody("");
    } catch { toast.error("Send failed"); } finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="mb-2 font-display text-3xl">EMAIL BLAST <Mail className="ml-1 inline text-yaza-accent" size={22} /></h2>
      <p className="mb-6 text-xs text-neutral-500">Plain-text blast to the whole YAZA list (via Resend — currently MOCKED / logged).</p>
      <label className={lbl}>Subject</label>
      <input className={field} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="blast-subject" />
      <label className={`${lbl} mt-4`}>Message</label>
      <textarea className={field} rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="blast-body" />
      <button onClick={send} disabled={sending} data-testid="send-blast" className="mt-6 rounded-none bg-yaza-accent px-8 py-3 text-sm font-bold uppercase tracking-widest text-black hover:bg-yaza-accentHover disabled:opacity-50">{sending ? "Sending…" : "Send Blast"}</button>
    </div>
  );
}

export function ImportTab() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/admin/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} new, updated ${data.updated}`);
    } catch { toast.error("Import failed — check CSV format"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="mb-2 font-display text-3xl">SHOPIFY IMPORT</h2>
      <p className="mb-6 text-xs text-neutral-500">Upload a Shopify product CSV export. Maps Title → strain name, Body (HTML) → description (verbatim), Handle → slug, Image Src → images.</p>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-yaza-border py-16 hover:border-yaza-accent" data-testid="import-dropzone">
        <UploadCloud size={32} className="text-yaza-accent" />
        <span className="text-sm">{busy ? "Importing…" : "Click to select products_export.csv"}</span>
        <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="import-file" />
      </label>
      {result && (
        <div className="mt-6 rounded-lg border border-yaza-border p-4 text-sm" data-testid="import-result">
          <p>Products parsed: <b>{result.products}</b></p>
          <p className="text-yaza-ready">Created: {result.created}</p>
          <p className="text-yaza-soon">Updated: {result.updated}</p>
        </div>
      )}
    </div>
  );
}
